import UsersGrid from '../../components/grids/UsersGrid'
import InactiveUsersGrid from '../../components/grids/InactiveUsersGrid'
import AddUserForm from '../../components/FormComps/Users/AddUserForm'
import EditUserForm from '../../components/FormComps/Users/EditUserForm'

import { useState, useEffect } from 'react'
import { useToast } from '../../contexts/ToastContext'

import {
  Drawer,
  DrawerContent,
  DrawerNavigation,
} from '@progress/kendo-react-layout'
import { FaTimesCircle } from 'react-icons/fa'
import { submitUser } from '../../data/userData'
import {
  getEmployees,
  deleteEmployee,
  updateEmployee,
  getInactiveEmployees,
  restoreEmployee,
} from '../../data/employeeData'
import {
  employeesGrid,
  inactiveEmployee,
  postUserTypes,
  updateEmployeeTypes,
} from '../../data/interfaces'

import { ApiErrorDetails, getApiErrorDetails } from '../../helpers/apiError'

import '../Styles.css'

interface UsersProps {
  allowedRoutes?: string[]
}

const Users = (props: UsersProps) => {
  const allowed = Array.isArray(props.allowedRoutes) ? props.allowedRoutes : []
  // Users.View only looks; Users.Edit or an administrator change.
  // Only an administrator may touch an administrator - the server enforces
  // both, this just keeps the controls that would be refused out of the way.
  const isAdministrator = allowed.includes('Portal.Administrator')
  const canEdit = isAdministrator || allowed.includes('Users.Edit')

  const [addUserError, setAddUserError] = useState<ApiErrorDetails | null>(null)
  const [userData, setUserData] = useState<employeesGrid[]>([])
  const [expanded, setExpanded] = useState(false)
  const [editFormVisible, setEditFormVisible] = useState(false)
  const [addFormVisible, setAddFormVisible] = useState(false)
  const [employeeId, setEmployeeId] = useState(0)
  const [showInactive, setShowInactive] = useState(false)
  const [inactiveData, setInactiveData] = useState<inactiveEmployee[]>([])
  const { showToast } = useToast()

  const handleAddForm = () => {
    setAddFormVisible((prevState) => (!prevState ? true : true))
    setEditFormVisible(false)
    handleExpanded()
  }

  const handleEditForm = (id: number) => {
    setEmployeeId(id)
    setEditFormVisible((prevState) => (!prevState ? true : true))
    setAddFormVisible(false)
    handleExpanded()
  }

  const updateUserHandler = (dataItem: updateEmployeeTypes, id: number) => {
    const userObj = {
      email: dataItem.email,
      permissions: dataItem.permissions,
      employeeId: id,
      employeeName: dataItem.employeeName,
      position: dataItem.position,
      subcontractor: dataItem.subcontractor,
      password: dataItem.password,
      adminRole: dataItem.adminRole,
      sortOrder: dataItem.sortOrder,
      worktypegroupdefaultId: dataItem.worktypegroupdefaultId,
      worktypedefaultId: dataItem.worktypedefaultId,
      hourlyRate: dataItem.hourlyRate,
      employeeTypeId: dataItem.employeeTypeId,
      mobilePhone: dataItem.mobilePhone,
      groupIds: dataItem.groupIds,
      directReportEmployeeIds: dataItem.directReportEmployeeIds,
      canViewAllUsers: dataItem.canViewAllUsers,
    }
    updateEmployee(userObj, id).then(() => {
      const fetchData = async () => {
        const userData = await getEmployees()
        setUserData(userData)
        handleExpanded()
        showToast('User succesfully updated', 'success')
      }
      fetchData()
    }).catch((error) => {
        handleExpanded()
        const { message, details } = getApiErrorDetails(error, "The user couldn't be updated.")
        showToast([message, ...details].join(' '), 'error')
      })
  }

  const newUserHandler = async (dataItem: postUserTypes) => {
    const userObj = {
      displayName: dataItem.displayName,
      position: dataItem.position,
      email: dataItem.email,
      subcontractor: dataItem.subcontractor,
      password: dataItem.password,
      confirmPassword: dataItem.confirmPassword,
      adminRole: dataItem.adminRole,
      hourlyRate: dataItem.hourlyRate,
      employeeTypeId: dataItem.employeeTypeId,
      mobilePhone: dataItem.mobilePhone,
    };

    try {
      await submitUser(userObj);

      const fetchData = async () => {
        const userData = await getEmployees();
        setUserData(userData);
      };
      fetchData();

      handleExpanded();
      showToast('User successfully added', 'success');
      setAddUserError(null);
    } catch (err) {
      // Leave the form open with the reason shown in it, so the details that
      // were typed in can be corrected rather than entered again.
      const error = getApiErrorDetails(err, "The user couldn't be added.");
      setAddUserError(error);
      showToast(error.message, "error");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const userData = await getEmployees()
      setUserData(userData)
    }

    fetchData()
  }, [])

  const deleteUserHandler = (id: number) => {
    deleteEmployee(id)
      .then(() => {
        setUserData((prevData) =>
          prevData.filter((user) => user.employeeId !== id)
        )
        handleExpanded()
        showToast('User succesfully deleted', 'success')  
      })
      .catch((error) => {
        // Handle error, for example, showing a toast message about the failure
        console.log('Error deleting user: ' + error.message)
        handleExpanded()
        showToast('Error deleting user', 'error')
      })
    
  }

  const showInactiveUsers = () => {
    getInactiveEmployees()
      .then((data) => {
        setInactiveData(data)
        setShowInactive(true)
      })
      .catch(() => showToast("Couldn't load deleted users", 'error'))
  }

  const restoreUserHandler = (id: number) => {
    restoreEmployee(id)
      .then(async () => {
        setInactiveData((prevData) =>
          prevData.filter((user) => user.employeeId !== id)
        )
        setUserData(await getEmployees())
        showToast('User successfully restored', 'success')
      })
      .catch(() => showToast('Error restoring user', 'error'))
  }

  const handleExpanded = () => {
    setExpanded((prevState) => !prevState)
    setAddUserError(null)

    if (editFormVisible) {
      setEditFormVisible(false)
    }
    if (addFormVisible) {
      setAddFormVisible(false)
    }
  }

  const handleSelect = () => {
    setExpanded(false)
  }

  return (
    <div>
      <Drawer
        expanded={expanded}
        position={'end'}
        width={750}
        mode='overlay'
        animation={{
          duration: 400,
        }}
        onOverlayClick={handleExpanded}
        onSelect={handleSelect}
      >
        <DrawerContent>
          <DrawerNavigation>
            {addFormVisible && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Add User</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <div className='form__wrapper'>
                  <AddUserForm
                    onUserSubmit={newUserHandler}
                    canGrantAdmin={isAdministrator}
                    error={addUserError}
                    onDismissError={() => setAddUserError(null)}
                  />
                </div>
              </>
            )}

            {editFormVisible && (
              <>
                <div className='form__title__container'>
                  <h1 className='form__title'>Edit User</h1>
                  <FaTimesCircle
                    className='closeform'
                    onClick={handleExpanded}
                  />
                </div>
                <EditUserForm
                  onEditSubmit={updateUserHandler}
                  onEmployeeId={employeeId}
                  onUserDelete={deleteUserHandler}
                  canEdit={canEdit}
                  isAdministrator={isAdministrator}
                />
              </>
            )}
          </DrawerNavigation>

          {showInactive ? (
            <InactiveUsersGrid
              onData={inactiveData}
              canRestore={canEdit}
              onBack={() => setShowInactive(false)}
              onRestore={restoreUserHandler}
            />
          ) : (
            <UsersGrid
              onAddToggle={handleAddForm}
              onEditToggle={handleEditForm}
              onInactiveToggle={showInactiveUsers}
              canAdd={canEdit}
              onData={userData}
            />
          )}
        </DrawerContent>
      </Drawer>
    </div>
  )
}

export default Users
